import DialogConfirmCommon from "@/components/common/DialogConfirm";
import AssignDriver from "@/components/common/orders/AssignDriver";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Orders, Vehicle } from "@/interface/entityInterface";
import { useOrderData } from "@/taxi-dispatch/orders/hooks/useOrderData";
import axiosInstance from "@/utils/axiosInstance";
import { format } from "date-fns";
import {
  Car,
  Clock,
  MapPinCheck,
  MapPinMinus,
  Phone,
  User,
} from "lucide-react";
import { Dispatch, FC, SetStateAction, useState } from "react";
import { useGetList, useRefresh } from "react-admin";
import { toast } from "sonner";

interface Props {
  item: Orders;
  assignByDriver: boolean;
  setCenter?: Dispatch<SetStateAction<{ lat: number; lng: number }>>;
  setVehicleId?: Dispatch<SetStateAction<number>>;
}

const OrderCard: FC<Props> = ({
  item,
  assignByDriver,
  setCenter,
  setVehicleId,
}) => {
  const refresh = useRefresh();
  const { drivers } = useOrderData();
  const { data: vehicles = [] } = useGetList<Vehicle>("vehicles", {
    pagination: { page: 1, perPage: 5000 },
  });
  const [isCancel, setCancel] = useState(false);
  const [orderId, setOrderId] = useState(0);
  const [loading, setLoading] = useState(false);

  const confirmCancel = async () => {
    setLoading(true);
    try {
      await axiosInstance.put(`/taxi-orders/${orderId}`, {
        status: "CANCELLED",
      });
      toast(`Order ${orderId} cancelled`);
      refresh();
    } catch (err) {
      toast(err.response.data.message);
    } finally {
      setLoading(false);
      setCancel(false);
      setOrderId(0);
    }
  };
  return (
    <Card key={item.id} className="border-0 shadow-none sm:shadow-md sm:border-1">
      <CardContent>
        <div className="flex items-center justify-between mb-2 ">
          <CardTitle>#{item.id}</CardTitle>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="flex items-center gap-x-1 text-gray-600">
              <MapPinCheck className="w-4.5 h-4.5 " />
              <span
                className={`line-clamp-1 text-sm ${
                  setCenter &&
                  "text-blue-500 cursor-pointer hover:underline underline-offset-4"
                }`}
                onClick={() => {
                  if (setCenter) {
                    setCenter({
                      lat: item.pickup_latitude,
                      lng: item.pickup_longitude,
                    });
                  }
                }}
              >
                {item.pickup}
              </span>{" "}
              {/* → {item.dropoff || "Not specified"} */}
            </p>
            {item.dropoff && (
              <p className="flex items-center gap-x-1 text-gray-600">
                <MapPinMinus className="w-4 h-4 " />
                <span className={`line-clamp-1 text-sm`}>
                  {item.dropoff}
                </span>{" "}
                {/* → {item.dropoff || "Not specified"} */}
              </p>
            )}
            <div className="flex items-center gap-x-2 text-xs text-gray-500 mt-1 mb-2">
              <div className="flex gap-x-1 items-center">
                <Clock className="w-4 h-4" />{" "}
                {format(item.pickup_time, "HH:mm")}{" "}
              </div>
              {item.client_phone && (
                <div className="flex gap-x-1 items-center">
                  <Phone className="w-4 h-4" /> {item.client_phone}
                </div>
              )}
              {item.client_name && (
                <div className="flex gap-x-1 items-center">
                  <User className="w-4 h-4" /> {item.client_name}
                </div>
              )}
              {item.vehicle_id && item.status === "GOING_TO_PICKUP" && (
                <div
                  className="flex gap-x-1 items-center text-blue-500 cursor-pointer"
                  onClick={() => {
                    if (setVehicleId) {
                      setVehicleId(item.driver.vehicle.id);
                    }
                  }}
                >
                  <Car className="w-4 h-4" /> {item.driver.vehicle.nickname}
                </div>
              )}
            </div>
            <div className="flex items-center justify-center gap-2">
              {item.status !== "GOING_TO_PICKUP" &&
                (!assignByDriver ? (
                  <DialogConfirmCommon
                    id={item.id || 0}
                    endpoint="taxi-orders"
                    endpoint2="assign-vehicle"
                    description={`Confirm this action?`}
                    propKey="vehicle_id"
                    dataProp="nickname"
                    dataOptions={vehicles}
                    selectedValue={item.vehicle_id || null}
                  />
                ) : (
                  <AssignDriver order={item} drivers={drivers} />
                ))}
              <Button
                className="py-0"
                variant="destructive"
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setCancel(true);
                  setOrderId(item.id);
                }}
              >
                Cancel order
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
      <Dialog open={isCancel} onOpenChange={setCancel}>
        <DialogContent className="sm:max-w-[350px]">
          <DialogTitle>Cancel order #{orderId}?</DialogTitle>
          <DialogDescription>
            Are you sure you want to cancel this order?
          </DialogDescription>
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => {
                setCancel(false);
                setOrderId(0);
              }}
            >
              Cancel
            </Button>
            <Button onClick={confirmCancel} disabled={loading}>
              {loading ? "Confirming..." : "Confirm"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default OrderCard;
